import { put, call, takeLatest, takeEvery, select, cancelled } from 'redux-saga/effects';
import {
  CASE_FETCH_MATCHES_BEGIN,
  CASE_FETCH_MATCHES_SUCCESS,
  CASE_FETCH_MATCHES_FAILURE,
  CASE_FETCH_MATCHES_DISMISS_FEEDBACK,
  CASE_CLEAR_MATCHES,
  CASE_CANCEL_FETCH_MATCHES,
} from './constants';
import api from 'common/api';
import { selectCaseDetails, selectUserSettings, withCurrentCaseId } from 'common/selectors';
import { getNonce } from 'utils/nonce';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';

export const fetchMatches = createPromiseAction(CASE_FETCH_MATCHES_BEGIN);

export const cancelFetchMatches = () => ({
  type: CASE_CANCEL_FETCH_MATCHES,
});

// Store for tracking cancellation state and AbortController
let isCancellationRequested = false;
let currentAbortController = null;

export function dismissFetchMatchesFeedback() {
  return {
    type: CASE_FETCH_MATCHES_DISMISS_FEEDBACK,
  };
}

export function clearMatches() {
  return {
    type: CASE_CLEAR_MATCHES,
  };
}

// worker Saga: will be fired on CASE_FETCH_MATCHES_BEGIN actions
export function* doFetchMatches(action) {
  const {
    payload: {
      caseId,
      searchTerm,
      searchLocation,
      files,
      omitFiles,
      compare = {},
      source,
      promptCategory,
      promptName,
    },
  } = action;

  if (!searchTerm) {
    return yield put({
      type: CASE_FETCH_MATCHES_SUCCESS,
      data: {
        results: [],
        isDeepSearchPerformed: false,
      },
    });
  }

  // Cancel any existing request
  if (currentAbortController) {
    currentAbortController.abort();
  }

  // Reset cancellation flag and create new AbortController
  isCancellationRequested = false;
  currentAbortController = new AbortController();

  const nonce = getNonce(6);

  try {
    const userSettings = yield select(selectUserSettings);
    const caseDetails = yield select(selectCaseDetails);

    // Build the URL with optional query parameters
    let url = `/cases/${caseId}/matches`;
    if (omitFiles !== undefined) {
      url += `?omitFiles=${omitFiles}`;
    }

    // Build the request payload
    const requestPayload = {
      nonce,
      search: searchTerm,
      filters: { folders: searchLocation, files },
      numMatches: (userSettings.aiSettings && userSettings.aiSettings.numMatches) || 1000,
      multiQuery:
        (userSettings.aiSettings && userSettings.aiSettings.multiQuery) ||
        (caseDetails.aiSettings &&
          caseDetails.aiSettings.matches &&
          caseDetails.aiSettings.matches.multiQuery) ||
        false,
      maxMarginalRelevance:
        (userSettings.aiSettings && userSettings.aiSettings.maxMarginalRelevance) ||
        (caseDetails.aiSettings &&
          caseDetails.aiSettings.matches &&
          caseDetails.aiSettings.matches.maxMarginalRelevance) ||
        false,
      index: (userSettings.aiSettings && userSettings.aiSettings.index) || '',
      useQueryRouter: (userSettings.aiSettings && userSettings.aiSettings.queryRouter) || false,
    };

    // Add source if provided (e.g., 'sidebar') to inform backend of request origin
    if (source) {
      requestPayload.source = source;
    }

    // Add compare field if it exists
    if (compare) {
      requestPayload.compare = compare;
    }

    // Add prompt metadata if provided
    if (promptCategory) {
      requestPayload.promptCategory = promptCategory;
    }
    if (promptName) {
      requestPayload.promptName = promptName;
    }

    const res = yield call(api.post, url, requestPayload, {
      signal: currentAbortController.signal,
    });

    // Check if cancellation was requested while the call was in progress
    if (isCancellationRequested) {
      // Request was cancelled after it completed - resolve as cancelled
      currentAbortController = null;
      yield call(resolvePromiseAction, action, { cancelled: true });
      return;
    }

    if (res && res.error) {
      // Clear the controller on error
      currentAbortController = null;
      yield put({
        type: CASE_FETCH_MATCHES_FAILURE,
        feedback: {
          message: 'feedback.fetchMatchesFailure',
          error: res.error,
        },
      });
      return yield call(rejectPromiseAction, action, res.error);
    }

    // Dispatch success action out of try/catch so that render errors are not catched.
    if (res.nonce === nonce) {
      // Clear the controller on successful completion
      currentAbortController = null;
      yield put({
        type: CASE_FETCH_MATCHES_SUCCESS,
        data: {
          results: res.results,
          searchHistoryId: res.searchHistoryId,
          isDeepSearchPerformed: true,
        },
      });
      yield call(resolvePromiseAction, action, res);
    }
  } catch (error) {
    // Clear the controller on error
    currentAbortController = null;

    // Check if it was an abort error (cancellation)
    if (
      error.name === 'AbortError' ||
      (error.message &&
        (error.message.includes('aborted') || error.message.includes('signal is aborted'))) ||
      isCancellationRequested
    ) {
      // Request was cancelled - resolve as cancelled to prevent promise rejection
      yield call(resolvePromiseAction, action, { cancelled: true });
      return;
    }

    // Handle other errors
    yield put({
      type: CASE_FETCH_MATCHES_FAILURE,
      feedback: {
        message: 'feedback.fetchMatchesFailure',
        error: error.message,
      },
    });
    yield call(rejectPromiseAction, action, error);
  } finally {
    // Handle saga cancellation
    if (yield cancelled()) {
      if (currentAbortController) {
        currentAbortController.abort();
        currentAbortController = null;
      }
      isCancellationRequested = true;
    }
  }
}

// worker Saga: will be fired on CASE_CANCEL_FETCH_MATCHES actions
// eslint-disable-next-line require-yield
export function* doCancelFetchMatches() {
  // Set the cancellation flag
  isCancellationRequested = true;

  // Abort the current HTTP request
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
}

/*
  Alternatively you may use takeEvery.

  takeLatest does not allow concurrent requests. If an action gets
  dispatched while another is already pending, that pending one is cancelled
  and only the latest one will be run.
*/
export function* watchFetchMatches() {
  yield takeLatest(fetchMatches, withCurrentCaseId(doFetchMatches));
}

export function* watchCancelFetchMatches() {
  yield takeEvery(CASE_CANCEL_FETCH_MATCHES, doCancelFetchMatches);
}

export function useFetchMatches() {
  const dispatch = useDispatch();

  const { matches, fetchMatchDocumentsPending, fetchDocumentsFeedback, searchHistoryId } =
    useSelector(
      (state) => ({
        matches: state.case.matches,
        fetchMatchDocumentsPending: state.case.fetchMatchDocumentsPending,
        fetchDocumentsFeedback: state.case.fetchDocumentsFeedback,
        searchHistoryId: state.case.searchHistoryId,
      }),
      shallowEqual,
    );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(fetchMatches(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissFetchMatchesFeedback());
  }, [dispatch]);

  const boundClearMatches = useCallback(() => {
    return dispatch(clearMatches());
  }, [dispatch]);

  const boundCancelAction = useCallback(() => {
    return dispatch(cancelFetchMatches());
  }, [dispatch]);

  return {
    matches,
    fetchMatches: boundAction,
    fetchMatchDocumentsPending,
    fetchDocumentsFeedback,
    searchHistoryId,
    dismissFetchMatchesFeedback: boundDismissFeedback,
    clearMatches: boundClearMatches,
    cancelFetchMatches: boundCancelAction,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_FETCH_MATCHES_BEGIN + '.TRIGGER':
      return {
        ...state,
        fetchMatchDocumentsPending: true,
        fetchDocumentsFeedback: action.feedback,
        // documents: [],
        matches: [],
        answers: null,
        isDeepSearchPerformed: false,
        searchHistoryId: null,
      };

    case CASE_CLEAR_MATCHES:
      return {
        ...state,
        // documents: [],
        matches: [],
        searchMatchesInput: [],
        answers: null,
        isDeepSearchPerformed: false,
        searchHistoryId: null,
      };

    case CASE_FETCH_MATCHES_SUCCESS:
      return {
        ...state,
        fetchMatchDocumentsPending: false,
        fetchDocumentsFeedback: action.feedback,
        matches: action.data.results,
        searchHistoryId: action.data.searchHistoryId,
        isDeepSearchPerformed: action.data.isDeepSearchPerformed,
      };

    case CASE_FETCH_MATCHES_FAILURE:
      return {
        ...state,
        fetchMatchDocumentsPending: false,
        // documents: [],
        matches: [],
        fetchDocumentsFeedback: action.feedback,
        isDeepSearchPerformed: false,
        searchHistoryId: null,
      };

    case CASE_FETCH_MATCHES_DISMISS_FEEDBACK:
      return {
        ...state,
        fetchDocumentsFeedback: null,
        searchHistoryId: null,
      };

    case CASE_CANCEL_FETCH_MATCHES:
      return {
        ...state,
        fetchMatchDocumentsPending: false,
        fetchDocumentsFeedback: null,
      };

    default:
      return state;
  }
}
